import React, { useEffect, useState } from 'react';
import { BiUserCircle } from 'react-icons/bi';
import GenericNavBar from 'newComponents/GenericNavBar';
import facebookIcon from 'assets/icons/facebook.svg';
import instagramIcon from 'assets/icons/instragam.svg';
import whatsappIcon from 'assets/icons/whatsapp.svg';
import youtubeIcon from 'assets/icons/youtube.svg';
import mobilePhoneIcon from 'assets/icons/mobilePhone.svg';
import mailIcon from 'assets/icons/Mail.svg';
import headsetIcon from 'assets/icons/new-headset.svg';
import {
  ConsultantDataReponse,
  getConsultants,
} from 'services/consultantsServices';
import phoneMask from 'utils/phoneMask';
import { useTour } from 'hooks/useTour';
import TourBalloon from 'newComponents/NewTour/TourBallon';
import {
  Card,
  CardContent,
  Columns,
  Container,
  DescriptionContainer,
  DescriptionText,
  DescriptionTitle,
  DetailTitle,
  EmailLink,
  HeadsetIcon,
  HeadsetRow,
  Hint,
  IconCircle,
  LeftCol,
  Line,
  Muted,
  RightCol,
  Rule,
  SocialFollowRow,
  SocialIconsGroup,
  SocialMediaIconLink,
  SocialMediasTitle,
  SocialMediaText,
  Spinner,
  SpinnerWrap,
  Stack,
  SuffixIcon,
  TableWrap,
} from './styles';

function fixPhoto(s: string): string {
  const t = s.trim();
  if (!t) return '';
  const j = t.indexOf('base64,');
  if (j !== -1) return t.slice(0, j + 7) + t.slice(j + 7).replace(/\s/g, '');
  return t.replace(/\s/g, '');
}

function FaleConosco() {
  return (
    <HeadsetRow>
      <IconCircle>
        <HeadsetIcon src={headsetIcon} alt="" aria-hidden />
      </IconCircle>
      <Stack>
        <DetailTitle>Fale conosco</DetailTitle>
        <Line>
          <span>
            <Muted>0800 866 1616</Muted> <Hint>(Demais regiões)</Hint>
          </span>
          <SuffixIcon src={mobilePhoneIcon} alt="" aria-hidden />
        </Line>
        <Line>
          <span>
            <Muted>4003 1616</Muted>{' '}
            <Hint>(Capitais e regiões metropolitanas)</Hint>
          </span>
          <SuffixIcon src={mobilePhoneIcon} alt="" aria-hidden />
        </Line>
      </Stack>
    </HeadsetRow>
  );
}

const avatarStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  flexShrink: 0,
  backgroundColor: '#e5e7ff',
};

const BusinessContact: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [hasConsultant, setHasConsultant] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const [consultant, setConsultant] = useState<ConsultantDataReponse>(
    {} as ConsultantDataReponse
  );

  const { showTour, tourType, tourStep, setTourType, setSteps } = useTour();

  const hasInfo = !!(
    consultant.name?.trim() ||
    consultant.phone?.trim() ||
    consultant.email?.trim() ||
    consultant.photo?.trim()
  );

  useEffect(() => {
    setTourType('businessContact');
    setSteps(1);
  }, [showTour, setTourType, setSteps]);

  useEffect(() => {
    setImgBroken(false);
  }, [consultant.photo]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const rows = await getConsultants();
      setLoading(false);

      if (!rows.length) {
        setHasConsultant(false);
        return;
      }

      const c = rows[0];
      const raw = c.photo;
      const photo =
        typeof raw === 'string' && raw.trim() ? fixPhoto(raw) : '';

      setConsultant({
        name: c.name ?? '',
        email: (c.email ?? '').replace('.invalid', ''),
        phone: c.phone ? phoneMask(c.phone) : '',
        consultantName: c.consultantName ?? '',
        photo,
      });
      setHasConsultant(true);
    }
    load();
  }, []);

  const tourActive =
    showTour && tourType === 'businessContact' && tourStep === 1;

  return (
    <GenericNavBar
      breadcrump="› Atendimento comercial"
      selected="Atendimento comercial"
      intro={() => true}
    >
      <Container>
        <DescriptionContainer>
          {loading && (
            <SpinnerWrap>
              <Spinner color="#0000ff" size={24} />
            </SpinnerWrap>
          )}
          {!loading && (
            <>
              <DescriptionTitle>Atendimento comercial</DescriptionTitle>
              <DescriptionText>
                Através deste canal você será direcionado ao atendimento da nossa
                área de <strong>RCR</strong> (Relacionamento Comercial Remoto)
                que poderá apoiá-lo.
              </DescriptionText>
            </>
          )}
        </DescriptionContainer>
        <Card>
          {loading && (
            <SpinnerWrap>
              <Spinner color="#0000ff" size={24} />
            </SpinnerWrap>
          )}
          {!loading && (
            <TableWrap showTour={tourActive}>
              <CardContent>
                <Columns>
                  {hasConsultant && !hasInfo && (
                    <LeftCol $full>
                      <FaleConosco />
                    </LeftCol>
                  )}
                  {hasConsultant && hasInfo && (
                    <>
                      <LeftCol>
                        {consultant.photo && !imgBroken ? (
                          <img
                            src={consultant.photo}
                            alt={consultant.name || 'Consultor'}
                            width={64}
                            height={64}
                            decoding="async"
                            style={avatarStyle}
                            onError={() => setImgBroken(true)}
                          />
                        ) : (
                          <IconCircle>
                            <BiUserCircle size={42} color="#0B29FB" />
                          </IconCircle>
                        )}
                        <Stack>
                          <DetailTitle>{consultant.name || ''}</DetailTitle>
                          {!!consultant.phone && (
                            <Line>
                              <Muted>{consultant.phone}</Muted>
                              <SuffixIcon
                                src={mobilePhoneIcon}
                                alt=""
                                aria-hidden
                              />
                            </Line>
                          )}
                          {!!consultant.email && (
                            <Line>
                              <EmailLink href={`mailto:${consultant.email}`}>
                                {consultant.email}
                              </EmailLink>
                              <SuffixIcon
                                src={mailIcon}
                                alt=""
                                aria-hidden
                              />
                            </Line>
                          )}
                        </Stack>
                      </LeftCol>
                      <Rule />
                    </>
                  )}
                  {(!hasConsultant || hasInfo) && (
                    <RightCol $full={!hasConsultant}>
                      <FaleConosco />
                    </RightCol>
                  )}
                </Columns>
              </CardContent>
              {tourActive && (
                <TourBalloon
                  title="Atendimento Comercial"
                  content="Aqui você encontra os dados do atendimento comercial responsável pelo seu contrato."
                  position={{
                    left: '232px',
                    right: '0',
                    top: '0',
                    arrowPosition: 'left',
                    maxWidth: 'unset',
                    boxShadow: false,
                    width: '262px',
                    transform: 'translateX(100%)',
                  }}
                />
              )}
            </TableWrap>
          )}
          <SocialMediasTitle>
            <SocialMediaText>
              Para atendimento, dê preferência aos nossos canais digitais
            </SocialMediaText>
          </SocialMediasTitle>
          <SocialFollowRow>
            <SocialMediaText>Acompanhe nossas redes sociais</SocialMediaText>
            <SocialIconsGroup>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.instagram.com/ultragazoficial"
              >
                <img src={instagramIcon} alt="Instagram Ultragaz" />
              </SocialMediaIconLink>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.facebook.com/ultragazoficial"
              >
                <img src={facebookIcon} alt="Facebook Ultragaz" />
              </SocialMediaIconLink>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://api.whatsapp.com/send?phone=558007010123&data=AWCy0nzRCL3VWxyEoEkMLrTAIB8E1SVaU_fnKplhIRLu6d8NKzNq1mpB5QM8vC7DLKzXS4povPn2qJCCO0rmt2G4M_idEbmRWSrkozOarahtTdKFti50bPeZlnqyo6qFDdIgUt36PdbDCpHW7y_Yx5t6j9CpQ9w1fZQyqMEqsm1bRTyksYB2nF6_i8cLE458cowVRse0try5QcxGm29e-HrLvth6ql5sKO_PkUnKika8e4fffYsHjiP0e4a1NcIE_NGA_rjUGh0iGd1O0wqH6HmmuqDzeya_3IJOXb36&source=FB_Page&app=facebook&entry_point=page_cta"
              >
                <img src={whatsappIcon} alt="WhatsApp Ultragaz" />
              </SocialMediaIconLink>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.youtube.com/channel/UCIJ0JnsSqv6LisxSgMsSuqg"
              >
                <img src={youtubeIcon} alt="YouTube Ultragaz" />
              </SocialMediaIconLink>
            </SocialIconsGroup>
          </SocialFollowRow>
        </Card>
      </Container>
    </GenericNavBar>
  );
};

export default BusinessContact;
